/**
 * Utility functions for detecting user's country/location
 */

import {
  getUserCountryPreference,
  detectedCountryStore,
  saveCountrySelection,
} from "#lib/stores/countryStore.js";

/**
 * Get country from various sources
 * @returns {Promise<string|null>} Country code (e.g., 'US', 'GB', 'DE')
 */
export async function detectUserCountry() {
  // Check if we're in browser environment
  if (typeof window === "undefined") {
    // console.log('Server-side execution, using fallback: SE');
    return "SE";
  }

  // 1. Check if user has already selected a country (highest priority)
  const userPreference = getUserCountryPreference();
  if (userPreference) {
    // console.log('User country from preference:', userPreference);
    return userPreference;
  }

  // 2. Try to infer from browser timezone
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = inferCountryFromTimezone(timezone);
    if (country) {
      detectedCountryStore.set(country);
      return country;
    }
  } catch {
    // Intl is unavailable or the timezone is unrecognised; fall through to SE.
  }

  // 3. Fallback to default (Sweden)
  detectedCountryStore.set("SE");
  return "SE";
}

/**
 * Get country code inferred from the browser's timezone (OS setting).
 * Exported for use in client-side cross-referencing with IP-based detection.
 * @returns {string|null} Country code, or null if timezone unavailable/unmapped
 */
export function getCountryFromBrowserTimezone() {
  if (typeof Intl === "undefined") return null;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return inferCountryFromTimezone(tz);
  } catch {
    return null;
  }
}

/**
 * Map a country to a broad plug region (US, UK, EU, AU) for reconciling
 * conflicting signals. Two countries in the same region are functionally
 * equivalent for shipping/plug purposes.
 * @param {string} countryCode
 * @returns {string} Region or 'UNKNOWN'
 */
function getRegionForCountry(countryCode) {
  if (!countryCode) return "UNKNOWN";
  // Inline region lists to avoid circular import with countryStore
  const US_SET = ["US", "CA", "MX", "PR", "VI", "GU", "AS", "MP"];
  const UK_SET = ["GB", "IE", "CY", "MT"];
  const EU_SET = [
    "AT",
    "BE",
    "BG",
    "HR",
    "CZ",
    "DK",
    "EE",
    "FI",
    "FR",
    "DE",
    "GR",
    "HU",
    "IT",
    "LV",
    "LT",
    "LU",
    "NL",
    "PL",
    "PT",
    "RO",
    "SK",
    "SI",
    "ES",
    "SE",
    "NO",
    "IS",
    "LI",
    "CH",
    "AL",
    "AD",
    "AM",
    "BA",
    "GE",
    "KZ",
    "XK",
    "MK",
    "MD",
    "MC",
    "ME",
    "RU",
    "SM",
    "RS",
    "TR",
    "UA",
    "VA",
    "BY",
  ];
  const AU_SET = ["AU", "NZ"];
  if (US_SET.includes(countryCode)) return "US";
  if (UK_SET.includes(countryCode)) return "UK";
  if (EU_SET.includes(countryCode)) return "EU";
  if (AU_SET.includes(countryCode)) return "AU";
  return "UNKNOWN";
}

/**
 * Reconcile IP-based country (Vercel) with browser timezone country.
 * Timezone is more reliable for users behind VPNs, roaming, or CDN POPs
 * that misidentify region. Prefer IP only when signals agree or timezone
 * is unavailable.
 * @param {string|null} vercelCountry
 * @returns {string|null}
 */
export function reconcileCountrySignals(vercelCountry) {
  const tzCountry = getCountryFromBrowserTimezone();

  // Both exist
  if (vercelCountry && tzCountry) {
    const vRegion = getRegionForCountry(vercelCountry);
    const tRegion = getRegionForCountry(tzCountry);
    // Signals agree on region → IP value is fine
    if (vRegion !== "UNKNOWN" && vRegion === tRegion) return vercelCountry;
    // Disagree → trust timezone (device-local, harder to fake than IP)
    if (tRegion !== "UNKNOWN") return tzCountry;
    return vercelCountry;
  }

  return vercelCountry || tzCountry || null;
}

/**
 * Infer country from browser timezone
 * @param {string} timezone - e.g., 'America/New_York'
 * @returns {string|null} Country code
 */
function inferCountryFromTimezone(timezone) {
  const timezoneCountryMap = {
    // US timezones
    "America/New_York": "US",
    "America/Los_Angeles": "US",
    "America/Chicago": "US",
    "America/Denver": "US",
    "America/Phoenix": "US",
    "America/Anchorage": "US",
    "Pacific/Honolulu": "US",

    // UK
    "Europe/London": "GB",

    // Major EU countries
    "Europe/Berlin": "DE",
    "Europe/Paris": "FR",
    "Europe/Madrid": "ES",
    "Europe/Rome": "IT",
    "Europe/Amsterdam": "NL",
    "Europe/Brussels": "BE",
    "Europe/Vienna": "AT",
    "Europe/Zurich": "CH",
    "Europe/Stockholm": "SE",
    "Europe/Oslo": "NO",
    "Europe/Copenhagen": "DK",
    "Europe/Helsinki": "FI",
    "Europe/Warsaw": "PL",
    "Europe/Prague": "CZ",
    "Europe/Budapest": "HU",
    "Europe/Bucharest": "RO",
    "Europe/Sofia": "BG",
    "Europe/Athens": "GR",
    "Europe/Lisbon": "PT",
    "Europe/Dublin": "IE",

    // Australia/NZ
    // 'Australia/Sydney': 'AU',
    // 'Australia/Melbourne': 'AU',
    // 'Australia/Brisbane': 'AU',
    // 'Australia/Perth': 'AU',
    // 'Australia/Adelaide': 'AU',
    // 'Pacific/Auckland': 'NZ',

    // Canada
    "America/Toronto": "CA",
    "America/Vancouver": "CA",
    "America/Montreal": "CA",

    // Mexico
    "America/Mexico_City": "MX",
    "America/Cancun": "MX",
    "America/Monterrey": "MX",
    "America/Tijuana": "MX",

    // US territories
    "America/Puerto_Rico": "PR",
    "Pacific/Guam": "GU",
  };

  return timezoneCountryMap[timezone] || null;
}

/**
 * Handle Vercel IP country detection and integrate with popup system
 * @param {string|null} vercelCountry - Country from Vercel IP detection
 * @returns {Promise<string>} Final country to use
 */
/**
 * Tier-1 confidence auto-accept: if IP country and browser timezone country
 * agree on the exact same country, silently save the selection and skip the
 * popup. Two independent signals agreeing is strong enough to commit; weaker
 * signals (region-only match, single signal) still require the popup so a
 * wrong auto-pick remains correctable.
 *
 * Synchronous so callers can run it during component script init, before
 * reactive blocks fire - avoids a brief popup flash on first visit.
 *
 * @param {string|null} vercelCountry
 * @returns {string|null} Auto-accepted country, or null if no autoAccept
 */
export function tryAutoAcceptCountry(vercelCountry) {
  if (!vercelCountry) return null;
  // Only autoAccept for users who haven't selected yet
  if (getUserCountryPreference()) return null;
  const tzCountry = getCountryFromBrowserTimezone();
  if (tzCountry && vercelCountry === tzCountry) {
    detectedCountryStore.set(vercelCountry);
    saveCountrySelection(vercelCountry, false);
    return vercelCountry;
  }
  return null;
}

export async function handleCountryDetection(vercelCountry = null) {
  // Check if user has already made a selection
  const userPreference = getUserCountryPreference();
  if (userPreference) {
    // console.log('Using user preference:', userPreference);
    return userPreference;
  }

  // Tier-1 autoAccept (IP === timezone)
  const autoAccepted = tryAutoAcceptCountry(vercelCountry);
  if (autoAccepted) return autoAccepted;

  // Cross-reference Vercel IP country with browser timezone.
  // Timezone beats IP for mobile users behind VPNs, carrier routing,
  // or CDN POPs that return misleading country codes.
  const reconciled = reconcileCountrySignals(vercelCountry);
  if (reconciled) {
    detectedCountryStore.set(reconciled);
    return reconciled;
  }

  // Fall back to full detection chain (includes IP service)
  return await detectUserCountry();
}
